<script setup>
import { provide, onMounted, watch } from 'vue'
import { RouterView } from 'vue-router' // Import useRoute
import { useWebViewDetection } from './composables/useWebViewDetection'

// Get the reactive detection results from the composable
const { isWebView, detectedApp, userAgent } = useWebViewDetection()

// --- Provide values to descendant components ---
provide('isWebView', isWebView)
provide('detectedApp', detectedApp)
provide('userAgent', userAgent)

// --- Optional: Add classes to the <body> ---
onMounted(() => {
    const updateBodyClasses = () => {
        if (typeof document !== 'undefined') {
            document.body.classList.remove('in-webview', 'not-in-webview')
            document.body.classList.remove(...Array.from(document.body.classList).filter((cls) => cls.startsWith('webview-')))

            if (isWebView.value) {
                document.body.classList.add('in-webview')
                if (detectedApp.value) {
                    const appSlug = detectedApp.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-+|-+$/g, '')
                    if (appSlug) {
                        document.body.classList.add(`webview-${appSlug}`)
                    }
                }
            } else {
                document.body.classList.add('not-in-webview')
            }
            console.log(`App Mounted: WebView=${isWebView.value}, App=${detectedApp.value}, UA=${userAgent.value}`)
        }
    }
    updateBodyClasses()
    watch([isWebView, detectedApp], updateBodyClasses)
})

// --- "Open in Browser" Button Logic ---
// No extra logic needed here, the link handles it.
// You could add a "dismiss" state if you wanted a close button.
// const showOpenInBrowser = ref(true); // Example if you wanted a dismiss feature
// const dismissBanner = () => { showOpenInBrowser.value = false; }
</script>

<template>
    <div v-if="isWebView" class="bg-[rgba(0,_0,_0,_0.8)] text-white h-screen min-h-full flex flex-col items-center justify-center text-[0.9rem] gap-2">
        <span>Using the app in {{ detectedApp || 'WebView' }}?</span>
        <span>Please open in your browser for a better experience.</span>
    </div>
    <router-view v-else></router-view>
</template>

<style>
/* --- Previous Styles --- */
#app {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
}

nav a {
    font-weight: bold;
    color: #2c3e50;
}
nav a.router-link-exact-active {
    color: #42b983;
}

/* --- Body Class Styles (Example) --- */
body.in-webview #app {
    /* Optional: specific styles when in any webview */
}
body.webview-linkedin .some-component {
    /* Optional: specific styles for LinkedIn webview */
}

/* --- "Open in Browser" Banner Styles --- */
.open-in-browser-banner {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 10px 15px;
    display: flex;
    justify-content: space-between; /* Adjust as needed: center, space-around */
    align-items: center;
    z-index: 1000; /* Ensure it's on top */
    box-sizing: border-box;
    font-size: 0.9rem;
}

.open-in-browser-banner span {
    margin-right: 10px;
}

.open-browser-button {
    display: inline-block;
    padding: 5px 12px;
    background-color: #42b983;
    color: white;
    text-decoration: none;
    border-radius: 4px;
    font-weight: bold;
    border: none;
    cursor: pointer;
    white-space: nowrap; /* Prevent button text wrapping */
}

.open-browser-button:hover {
    background-color: #36a46e;
}

/* Optional Close Button Styling */
/* .close-banner-button {
  background: none;
  border: none;
  color: #ccc;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 5px;
}
.close-banner-button:hover {
  color: white;
} */
</style>
